import type { PermCodename } from '@type/permissions'

/**
 * A team-scoped role. Mirrors the backend rbac.Role serializer output.
 * `is_system` distinguishes the seeded Admin/Member roles from
 * customer-created custom roles. Admin's permissions are locked to
 * the full set; Member's permissions are editable; custom roles are
 * fully editable.
 */
export interface Role {
  id: number
  name: string
  description: string
  permissions: PermCodename[]
  is_system: boolean
  member_count: number
  created_at: string
  updated_at: string
}

/**
 * The seeded system Admin role. Admin grants every permission, so
 * assigning it alongside other roles is redundant — the role pickers
 * treat it as mutually exclusive with all other roles, and the backend
 * rejects Admin+other combinations. Name-matching mirrors the backend's
 * `Role.is_admin_role` (`is_system and name == SYSTEM_ADMIN_NAME`).
 */
export const isSystemAdminRole = (role: Pick<Role, 'is_system' | 'name'>): boolean =>
  role.is_system && role.name === 'Admin'

export interface RoleCreatePayload {
  name: string
  description?: string
  permissions: PermCodename[]
}

export type RoleUpdatePayload = Partial<RoleCreatePayload>

/**
 * One entry in the permission catalog — what the role-editor checkbox
 * tree renders for each permission. Domain grouping happens client-side
 * by bucketing entries on their `domain` field.
 *
 * `bypasses_acl` flags perms that explicitly override per-row Workflow
 * Privacy / AR ACL (currently `workflow.view_all` and `request.view_all`
 * — the auditor-tier overrides). The role editor surfaces these with a
 * warning chip + confirmation when ticked, since granting one can
 * silently make restricted workflows visible to the role.
 */
export interface PermissionCatalogEntry {
  codename: PermCodename
  domain: string
  domain_label: string
  label: string
  description: string
  admin_only: boolean
  bypasses_acl: boolean
  /**
   * True for permissions that exist in the catalog but should be
   * hidden from the role-editor picker. Used for internal codenames
   * (pipeline.*) and legacy umbrella perms (accounting.view) — the
   * UI calls the former "workflows" and the latter is implied by
   * accounting.manage. Filter these out when rendering the tree.
   */
  hidden_from_role_editor: boolean
  /**
   * Other codenames that GRANT this one via the backend's PERM_IMPLIES.
   * Ticking any of them makes this permission effective whether or not it
   * is ticked itself — so the role editor shows it checked and locked
   * rather than offering a checkbox that silently does nothing.
   */
  implied_by?: PermCodename[]
}
